import type { ReactNode } from 'react';

import { Empty } from './Empty.js';
import { Skeleton } from './Skeleton.js';
import { cn } from './cn.js';

/*
 * Таблица списка. Логика (колонки как данные, сортируемые заголовки с aria-sort,
 * скелет на первой загрузке, скрытие колонок на узких экранах) взята из Table.tsx
 * референса Crowdpay; стили переписаны на утилиты Tailwind по макету (.tbl), а
 * зависимости от i18n и тёмной темы убраны - их у нас нет.
 *
 * Сортировка и пагинация здесь серверные: компонент только рисует состояние и
 * сообщает о клике, а запрос перевыпускает страница.
 */

/** Брейкпоинт, ниже которого колонка скрывается. */
export type TableBreakpoint = 'sm' | 'md' | 'lg' | 'xl' | 'never';

export interface TableSort {
  field: string;
  dir: 'asc' | 'desc';
}

export interface TableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T, rowIndex: number) => ReactNode;
  align?: 'left' | 'center' | 'right';
  mono?: boolean;
  /** Класс ширины колонки, напр. 'w-40'. Раскладка фиксированная (table-fixed). */
  width?: string;
  hideBelow?: TableBreakpoint;
  cellClassName?: string;
  /** Делает заголовок переключателем сортировки. Требует sort/onSortChange у таблицы. */
  isSortable?: boolean;
  /** Имя поля сортировки в API. По умолчанию - id колонки. */
  sortField?: string;
}

/** Атрибуты `data-*` строки - хэндлы тестов: id сущности, состояние. */
export type TableRowAttributes = Partial<Record<`data-${string}`, string>>;

export interface TableProps<T> {
  columns: ReadonlyArray<TableColumn<T>>;
  rows: ReadonlyArray<T>;
  getRowKey: (row: T, rowIndex: number) => string | number;
  /** `data-*` строки (`data-row-id`, `data-status`) - для селекторов тестов. */
  getRowAttributes?: (row: T, rowIndex: number) => TableRowAttributes;
  onRowClick?: (row: T, rowIndex: number) => void;
  footer?: ReactNode;
  emptyLabel?: ReactNode;
  isLoading?: boolean;
  skeletonRows?: number;
  /** Текущая сортировка - для индикатора и aria-sort. */
  sort?: TableSort;
  /** Вызывается с полем сортировки нажатой колонки; направление решает вызывающий. */
  onSortChange?: (field: string) => void;
  /** Класс всех ячеек тела, напр. 'py-3' для строк в две строки текста. */
  cellClassName?: string;
  className?: string;
}

const HIDE_CLASS: Record<Exclude<TableBreakpoint, 'never'>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
};

const DEFAULT_SKELETON_ROWS = 8;

const resolveHideClass = <T,>(column: TableColumn<T>): string | undefined => {
  if (!column.hideBelow || column.hideBelow === 'never') return undefined;
  return HIDE_CLASS[column.hideBelow];
};

const resolveCellClass = <T,>(column: TableColumn<T>): string =>
  cn(
    column.align === 'right' && 'text-right tabular-nums whitespace-nowrap',
    column.align === 'center' && 'text-center',
    column.mono && 'font-mono text-xs text-ink-600',
    resolveHideClass(column),
    column.cellClassName,
  );

const SORT_GLYPH = { asc: '▲', desc: '▼' } as const;

export const Table = <T,>({
  columns,
  rows,
  getRowKey,
  getRowAttributes,
  onRowClick,
  footer,
  emptyLabel = 'Ничего не найдено',
  isLoading = false,
  skeletonRows = DEFAULT_SKELETON_ROWS,
  sort,
  onSortChange,
  cellClassName,
  className,
}: TableProps<T>) => {
  const showSkeleton = isLoading && rows.length === 0;
  const isEmpty = !showSkeleton && rows.length === 0;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-ink-200 bg-white',
        className,
      )}
    >
      <table
        className="w-full table-fixed border-collapse text-[13px]"
        aria-busy={showSkeleton || undefined}
      >
        <thead>
          <tr>
            {columns.map((column) => {
              const isSortable =
                Boolean(column.isSortable) && onSortChange != null;
              const field = column.sortField ?? column.id;
              const isActive = isSortable && sort?.field === field;

              let ariaSort: 'ascending' | 'descending' | 'none' | undefined;
              if (isSortable)
                ariaSort = isActive
                  ? sort?.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none';

              return (
                <th
                  key={column.id}
                  aria-sort={ariaSort}
                  className={cn(
                    'border-b border-ink-200 bg-ink-50 px-3.5 py-2.5 text-left',
                    'text-[11.5px] font-[550] tracking-[0.04em] whitespace-nowrap text-ink-500 uppercase',
                    column.align === 'right' && 'text-right',
                    column.align === 'center' && 'text-center',
                    column.width,
                    resolveHideClass(column),
                  )}
                >
                  {isSortable ? (
                    <button
                      type="button"
                      onClick={() => onSortChange(field)}
                      // uppercase повторяется намеренно: браузер сбрасывает
                      // text-transform у <button>, и без него сортируемые
                      // заголовки набирались бы строчными, а остальные - заглавными.
                      className="inline-flex cursor-pointer items-center gap-1 uppercase select-none hover:text-ink-800"
                    >
                      {column.header}
                      <span aria-hidden className="text-[10px] text-ink-400">
                        {isActive ? SORT_GLYPH[sort?.dir ?? 'asc'] : '↕'}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {showSkeleton &&
            Array.from({ length: skeletonRows }).map((_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} aria-hidden>
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      'h-11 border-b border-ink-150 px-3.5 align-middle',
                      cellClassName,
                      resolveHideClass(column),
                    )}
                  >
                    <Skeleton
                      className={cn(
                        'w-full max-w-[140px]',
                        column.align === 'right' && 'ml-auto',
                        column.align === 'center' && 'mx-auto',
                      )}
                    />
                  </td>
                ))}
              </tr>
            ))}

          {isEmpty && (
            <tr>
              <td colSpan={columns.length} className="border-b-0 p-0">
                <Empty>{emptyLabel}</Empty>
              </td>
            </tr>
          )}

          {!showSkeleton &&
            rows.map((row, rowIndex) => (
              <tr
                key={getRowKey(row, rowIndex)}
                {...getRowAttributes?.(row, rowIndex)}
                onClick={
                  onRowClick ? () => onRowClick(row, rowIndex) : undefined
                }
                className={cn(
                  'transition-colors last:[&>td]:border-b-0',
                  onRowClick && 'cursor-pointer hover:bg-ink-50',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      'h-11 border-b border-ink-150 px-3.5 align-middle text-ink-800',
                      cellClassName,
                      resolveCellClass(column),
                    )}
                  >
                    {column.cell(row, rowIndex)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>

      {footer != null && (
        <div className="border-t border-ink-150 bg-ink-50 px-4 py-2.5 text-xs text-ink-500">
          {footer}
        </div>
      )}
    </div>
  );
};
